/**
 * What the Safety Recommendations chats are told: the register chat sees the search as it stands,
 * one recommendation's chat sees that recommendation in full.
 *
 * Both are plain text rather than JSON: the model reads prose better, and a person reading the
 * conversation can check what the model was given.
 */

import {
    SafetyRecommendationDTO,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationLinkedReportDTO,
    SafetyRecommendationStatsDTO,
} from '../../@types/safety-recommendation';
import {RegisterFilters} from '../../stores/safety-recommendation-register.store';
import {ageLabel, isAddressedToFaa, STATUS_GROUP_LABELS, STATUS_GROUP_ORDER} from './recommendations';

/** The most recommendations the register chat is given; past this the user is told to narrow the search. */
export const REGISTER_CONTEXT_LIMIT = 200;
/** The register chat gets each row's letters only when the search is at most this narrow. */
export const REGISTER_LETTER_LIMIT = 30;

const LETTER_CHARS = 1500;
const TEXT_CHARS = 350;

const oneLine = (text: string): string => text.replace(/\s+/g, ' ').trim();

const shorten = (text: string, limit: number): string =>
    text.length <= limit ? text : `${text.slice(0, limit)}… [shortened]`;

/** The filters in words, so the model knows what the set it was given leaves out. */
export const describeRegisterFilters = (filters: RegisterFilters): string => {
    const parts = [
        filters.text.trim() ? `matching "${filters.text.trim()}"` : null,
        filters.statusGroups.length > 0
            ? `status ${filters.statusGroups.map((group) => STATUS_GROUP_LABELS[group]).join(' or ')}` : null,
        filters.addressedTo === 'FAA' ? 'addressed to the FAA' : null,
        filters.addressedTo === 'NOT_FAA' ? 'not addressed to the FAA' : null,
        filters.sources.length > 0 ? `from ${filters.sources.join(', ')}` : null,
        filters.issuedFromYear !== null ? `issued from ${filters.issuedFromYear}` : null,
        filters.issuedToYear !== null ? `issued up to ${filters.issuedToYear}` : null,
        filters.includeSuperseded ? 'including superseded' : 'superseded left out',
    ].filter((part): part is string => Boolean(part));
    return parts.join('; ');
};

const statusLine = (rec: SafetyRecommendationDTO): string => (isAddressedToFaa(rec)
    ? `FAA: ${rec.faaStatus || 'status unknown'}${rec.faaClosedDate ? ` (closed ${rec.faaClosedDate})` : ''}`
    : `Not addressed to the FAA; overall: ${rec.overallStatus || 'unknown'}`);

/**
 * Where the recommendation sits in the taxonomy, and who says so. The origin is named because the
 * chat must not present our tagging as the NTSB's coding when it answers.
 */
const taxonomyLine = (rec: SafetyRecommendationDTO): string | null => {
    if (!rec.taxonomyTags || rec.taxonomyTags.length === 0) {
        return null;
    }
    const named = rec.taxonomyTags.map((tag) =>
        `${tag.category} / ${tag.leaf} (${tag.origin === 'NTSB' ? 'NTSB coded' : 'ISAM tagged'})`);
    return `taxonomy: ${Array.from(new Set(named)).join('; ')}`;
};

const recommendationLine = (rec: SafetyRecommendationDTO, today: Date, withLetters: boolean): string => {
    const details = [
        rec.source,
        statusLine(rec),
        rec.issuedDate ? `issued ${rec.issuedDate}` : null,
        ageLabel(rec, today) || null,
        rec.priority === 'CLASS I' ? 'CLASS I (urgent)' : rec.priority || null,
        rec.issuingCaseNumber ? `investigation ${rec.issuingCaseNumber}` : null,
        rec.otherAddressees.length > 0
            ? `also to: ${rec.otherAddressees.map((a) => `${a.name} (${a.status || 'status unknown'})`).join('; ')}`
            : null,
        rec.superseded ? `superseded by ${rec.supersededBy.map((s) => s.number).join(', ')}` : null,
        rec.timesReiterated ? `reiterated ${rec.timesReiterated}x` : null,
        taxonomyLine(rec),
    ].filter((part): part is string => Boolean(part));
    const text = oneLine(rec.text || '(no text)');
    const lines = [
        `- ${rec.number} [${details.join(' | ')}]`,
        `  Text: ${withLetters ? text : shorten(text, TEXT_CHARS)}`,
    ];
    if (withLetters && rec.assessment?.contents) {
        lines.push(`  NTSB assessment (${rec.assessment.date}): ${shorten(oneLine(rec.assessment.contents), LETTER_CHARS)}`);
    }
    if (withLetters && rec.latestResponse?.contents) {
        lines.push(`  Latest response from ${rec.latestResponse.addressee || 'the addressee'} (${rec.latestResponse.date}): `
            + shorten(oneLine(rec.latestResponse.contents), LETTER_CHARS));
    }
    return lines.join('\n');
};

interface RegisterContextArgs {
    /** The page the table shows, newest first. */
    rows: SafetyRecommendationDTO[];
    /** The counts over everything the filters match, not just this page. */
    stats: SafetyRecommendationStatsDTO;
    filters: RegisterFilters;
    today: Date;
}

/**
 * The register chat's context: the counts over everything that matches, then the recommendations
 * themselves. The counts come first because they cover the whole result set, while the rows are
 * only what could be carried.
 */
export const formatRegisterContext = ({rows, stats, filters, today}: RegisterContextArgs): string => {
    const included = rows.slice(0, REGISTER_CONTEXT_LIMIT);
    const withLetters = included.length <= REGISTER_LETTER_LIMIT;
    const statusCounts = STATUS_GROUP_ORDER
        .filter((group) => (stats.byStatusGroup[group] ?? 0) > 0)
        .map((group) => `${STATUS_GROUP_LABELS[group]}: ${stats.byStatusGroup[group]}`)
        .join('; ');
    const years = stats.byIssuedYear
        .map((year) => ({
            year: year.year,
            count: STATUS_GROUP_ORDER.reduce((total, group) => total + (year.byStatusGroup[group] ?? 0), 0),
        }))
        .filter((year) => year.count > 0)
        .map((year) => `${year.year}: ${year.count} issued`)
        .join('; ');

    const header = [
        'Context: Safety Recommendations',
        '',
        `${stats.total} recommendations match the current search. Status is read from the FAA's side: where the FAA is `
        + 'an addressee it is the FAA\'s own status, otherwise the overall status across every addressee.',
        `Active filters: ${describeRegisterFilters(filters)}`,
        statusCounts ? `Status of everything that matches: ${statusCounts}` : null,
        years ? `Issued by year: ${years}` : null,
    ].filter((part): part is string => part !== null);

    const notes: string[] = [];
    if (rows.length > REGISTER_CONTEXT_LIMIT) {
        notes.push(`NOTE: the newest ${REGISTER_CONTEXT_LIMIT} of the ${stats.total} that match are listed below. `
            + 'Narrow the search to cover the rest.');
    } else if (stats.total > rows.length) {
        notes.push(`NOTE: the ${rows.length} on the page shown are listed below, of the ${stats.total} that match. `
            + 'The counts above cover them all; the recommendations themselves do not.');
    }
    if (!withLetters) {
        notes.push(`NOTE: ${included.length} recommendations are listed, so their letters are left out and long texts `
            + `are shortened. A search narrowed to ${REGISTER_LETTER_LIMIT} or fewer carries each one's assessment and `
            + 'latest response.');
    }

    return [
        header.join('\n'),
        ...notes,
        included.map((rec) => recommendationLine(rec, today, withLetters)).join('\n\n'),
    ].filter((part) => part.length > 0).join('\n\n');
};

interface RecommendationContextArgs {
    rec: SafetyRecommendationDTO;
    /** Every letter about it, oldest first. */
    letters: SafetyRecommendationLetterDTO[];
    /** The ISAM reports tied to it that the reader may see. */
    reports: SafetyRecommendationLinkedReportDTO[];
    today: Date;
}

/** One recommendation's chat context: the recommendation in full, its correspondence and its reports. */
export const formatRecommendationContext = ({rec, letters, reports, today}: RecommendationContextArgs): string => {
    const parts = [
        `Context: ${rec.source} Safety Recommendation ${rec.number}`,
        '',
        recommendationLine(rec, today, true),
    ];
    if (rec.caseSummary) {
        parts.push(`Investigation ${rec.issuingCaseNumber || ''} synopsis: ${oneLine(rec.caseSummary)}`.trim());
    }
    if (rec.caseProbableCause) {
        parts.push(`Probable cause: ${oneLine(rec.caseProbableCause)}`);
    }
    if (reports.length > 0) {
        parts.push(`ISAM reports from the investigations tied to it: ${reports
            .map((report) => `${report.uniqueId} (${report.relation === 'REITERATED' ? 'reiterated it' : 'issued it'})`)
            .join(', ')}`);
    }
    if (letters.length > 0) {
        parts.push(['Correspondence, oldest first:', ...letters.map((letter) => {
            const between = letter.fromIssuer
                ? `NTSB to ${letter.addressee || 'the addressee'}`
                : `${letter.addressee || 'the addressee'} to the NTSB`;
            const type = letter.type ? ` (${letter.type})` : '';
            return `${letter.date || 'undated'} ${between}${type}: ${shorten(oneLine(letter.contents || '(no text)'), LETTER_CHARS)}`;
        })].join('\n'));
    }
    return parts.join('\n\n');
};
